import type { ExploreResult } from "@/types";

const OFFER_ENVELOPE_RE = /<<offer:(\{[\s\S]*?\})>>/g;

export interface ParsedOffer {
  url: string;
  title: string;
  company: string;
  location?: string;
  compensation?: string;
  source?: string;
  why?: string;
  postedHint?: string;
  ats?: string;
  verification?: string;
}

/** Extract offer envelopes from streamed AI text. */
export function parseOfferEnvelopes(text: string): ParsedOffer[] {
  const offers: ParsedOffer[] = [];
  let match: RegExpExecArray | null;

  const re = new RegExp(OFFER_ENVELOPE_RE.source, "g");
  while ((match = re.exec(text)) !== null) {
    try {
      const parsed = JSON.parse(match[1]) as ParsedOffer;
      if (parsed.url && parsed.title && parsed.company) {
        offers.push(parsed);
      }
    } catch {
      // skip malformed envelope
    }
  }

  return offers;
}

/** Strip offer envelope lines from reasoning text shown to the user. */
export function stripOfferEnvelopes(text: string): string {
  return text
    .replace(/<<offer:\{[\s\S]*?\}>>/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Map a parsed AI offer to an ExploreResult for the results list. */
export function offerToExploreResult(
  offer: ParsedOffer,
  index: number,
): ExploreResult {
  return {
    portal: offer.source ?? "ai-search",
    url: offer.url,
    title: offer.title,
    company: offer.company,
    location: offer.location,
    compensation: offer.compensation,
    postedAt: offer.postedHint,
    why: offer.why,
    source: offer.source ?? "ai-search",
    verification: offer.verification,
    id: `${offer.url}-${index}`,
  };
}

/** Incrementally parse new text chunk and return newly found offers. */
export function consumeOfferStream(
  buffer: string,
  seenUrls: Set<string>,
): { reasoning: string; newOffers: ExploreResult[]; buffer: string } {
  const offers = parseOfferEnvelopes(buffer);
  const newOffers: ExploreResult[] = [];

  for (let i = 0; i < offers.length; i++) {
    const offer = offers[i];
    if (seenUrls.has(offer.url)) continue;
    seenUrls.add(offer.url);
    newOffers.push(offerToExploreResult(offer, i));
  }

  return {
    reasoning: stripOfferEnvelopes(buffer),
    newOffers,
    buffer,
  };
}
